import { ServiceLocator } from '../../app/ServiceLocator';
import { ILongRunningAction, LongRunningActionState, LongRunningActionType } from '../models/ILongRunningAction';
import { GetActionInfoRequest } from '../ServerApi/requests/GetActionInfoRequest';
import { IActionInfo, IGetActionInfoResult } from '../ServerApi/types/IActionInfo';
import { IActionResult } from '../ServerApi/types/IActionResult';
import { IResult } from '../ServerApi/types/IResponse';
import { ErrorNotifyingServer } from './ErrorNotifyingServer';
import { IPoller, PollService } from './PollService';
import { ErrorReason } from './ServerService';

class LongRunningActionService {

  private StringToStateDict: {[stringType: string]: LongRunningActionState} = {
    queued : LongRunningActionState.Queued,
    inProgress : LongRunningActionState.InProgress,
    completed: LongRunningActionState.Complete
  };

  private StringToTypeDict: {[stringType: string]: LongRunningActionType} = {
    deleteSites: LongRunningActionType.DeleteSites
  };

  /**
   * Track the progress of a LongRunningAction
   * The promise will be fullfilled with the LongRunningAction when it is complete
   * The promise will be rejected with ResourceNotFound if there is no action of given id
   */
  public trackAsyncJobProgress(id: string): IPoller<ILongRunningAction> {
    const request = (new GetActionInfoRequest({actionId: id}));

    const isComplete = (result: IGetActionInfoResult) => {
      if (!result.actions || !result.actions.length) {
        return true;
      } else {
        return this.StringToStateDict[result.actions[0].state] === LongRunningActionState.Complete;
      }
    };

    const actionInfoPoller: IPoller<IGetActionInfoResult> = ServiceLocator.get<PollService>('PollService').poll(request, isComplete);

    const lrActionPromise: ng.IPromise<any> = actionInfoPoller.promise
      .then((result: IGetActionInfoResult) => {
        if (!result.actions || !result.actions.length) {
          return ServiceLocator.get<ng.IQService>('$q').reject(ErrorReason.ResourceNotFound);
        }

        return ServiceLocator.get<ng.IQService>('$q').when(this.toLongRunningAction(result.actions[0]));
      })
      .progress((result: IGetActionInfoResult) => {
        return this.toLongRunningAction(result.actions[0]);
      });

    // We have to return a new IPoller because type of the promise changes
    const lrActionPoller: IPoller<ILongRunningAction> = {
      promise: lrActionPromise,
      cancel: actionInfoPoller.cancel
    };
    return lrActionPoller;
  }

  // Track the progress of a LongRunningAction if an action id exists in the result passed in
  public startProgressTrackingForResult<TResult extends IResult>(): (result: IActionResult) => ng.IPromise<TResult> {
    return (result: IActionResult): ng.IPromise<TResult> => {
      if (!result.actionId) {
        return ServiceLocator.get<ng.IQService>('$q').reject(result);
      }

      const poller = this.trackAsyncJobProgress(result.actionId);

      return poller.promise.then((longRunningAction: ILongRunningAction) => {
        // Due to existing differences in API response format, it is possible that
        // longRunningAction.response is either IResponse type or IResult type.
        // If it's IReponse type, then there would be a property called 'result' to return,
        // otherwise longRunningAction.response can be returned directly.
        return longRunningAction.response?.result || longRunningAction.response;
      });
    };
  }

  // Convert a server result to an ILongRunningAction
  private toLongRunningAction(actionInfo: IActionInfo): ILongRunningAction {
    const action: ILongRunningAction = {
      id: actionInfo.id,
      type: this.StringToTypeDict[actionInfo.type],
      state: this.StringToStateDict[actionInfo.state],
      percentComplete: actionInfo.percentComplete,
      response: actionInfo.response,
      succeeded: actionInfo.succeeded
    };
    return action;
  }

  // Delay promise resolve by 2000ms
  public delayResultForSolr<T>(): (result: T) => Promise<T> {
    return (result: T): Promise<T> => {
      return new Promise(resolve =>
        setTimeout(() => resolve(result), ErrorNotifyingServer.ResponseDelay)
      );
    };
  }
}

export const LongRunningAction = new LongRunningActionService();
